import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import API from '../config/api';

const LocationContext = createContext(null);

const STORAGE_KEY = 'dmv_user_location';

export function LocationProvider({ children }) {
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [location, setLocationState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Load location on mount
  useEffect(() => {
    loadLocation();
  }, [isAuthenticated, user]);

  const loadLocation = async () => {
    setLoading(true);

    // 1. Try logged-in user's saved location from DB
    if (isAuthenticated && user?.neighborhood) {
      setLocationState({
        neighborhood: user.neighborhood,
        city: user.city || '',
        region: user.region || user.state || '',
        country: user.country || '',
        lat: user.lat,
        lng: user.lng,
      });
      setLoading(false);
      return;
    }

    // 2. Try localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.neighborhood) {
          setLocationState(parsed);
          setLoading(false);
          return;
        }
      }
    } catch {}

    // 3. No location set
    setLocationState(null);
    setLoading(false);
  };

  const setLocation = useCallback(async (newLocation) => {
    setLocationState(newLocation);

    // Save to localStorage (always, for persistence)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocation));
    } catch {}

    // Save to DB if logged in
    if (isAuthenticated) {
      try {
        await fetch(`${API}/users/location`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(newLocation),
        });
      } catch (e) {
        console.error('Failed to save location to DB:', e);
      }
    }
  }, [isAuthenticated, getAuthHeaders]);

  const clearLocation = useCallback(() => {
    setLocationState(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const openLocationPicker = useCallback(() => setShowLocationPicker(true), []);
  const closeLocationPicker = useCallback(() => setShowLocationPicker(false), []);

  // Auto-detect location via browser geolocation (only if user opts in)
  const detectLocation = useCallback(async () => {
    if (!navigator.geolocation) return null;

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            // Reverse geocode using backend proxy
            const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
            const res = await fetch(
              `${API_BASE}/api/location/reverse?lat=${latitude}&lng=${longitude}`
            );
            const data = await res.json();
            resolve(data);
          } catch {
            resolve(null);
          }
        },
        () => resolve(null),
        { timeout: 8000 }
      );
    });
  }, []);

  const displayLabel = location
    ? `${location.neighborhood}${location.city ? `, ${location.city}` : ''}`
    : null;

  return (
    <LocationContext.Provider value={{
      location,
      setLocation,
      clearLocation,
      loading,
      displayLabel,
      detectLocation,
      showLocationPicker,
      openLocationPicker,
      closeLocationPicker,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useUserLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useUserLocation must be used within LocationProvider');
  return ctx;
}
